import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react';
import {
  QuestionnaireResponse,
  Patient,
  QuestionnaireResponseItem,
  Questionnaire,
} from '@medplum/fhirtypes';
import { PRENATAL_PERIOD_QUESTIONNAIRE_NAME } from '@/consts/questionnaires.consts';
import { getReferenceString } from '@medplum/core';

interface UsePrenatalPeriodResponseResult {
  questionnaire: Questionnaire | undefined;
  questionnaireResponse: QuestionnaireResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  deleteResponse: () => Promise<void>;
  isIVFConception: boolean;
}

export const usePrenatalPeriodResponse = (
  patient: Patient | undefined
): UsePrenatalPeriodResponseResult => {
  const medplum = useMedplum();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | undefined>(
    undefined
  );
  const [questionnaireResponse, setQuestionnaireResponse] =
    useState<QuestionnaireResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const questionnaire = await medplum.searchOne('Questionnaire', {
        name: PRENATAL_PERIOD_QUESTIONNAIRE_NAME,
      });

      if (!questionnaire) {
        setQuestionnaire(undefined);
        setQuestionnaireResponse(null);
        setLoading(false);
        return;
      }

      setQuestionnaire(questionnaire);
      const response = await medplum.searchOne('QuestionnaireResponse', {
        questionnaire: getReferenceString(questionnaire),
        subject: getReferenceString(patient),
        _sort: '-authored',
      });

      setQuestionnaireResponse(response || null);
    } catch (err) {
      console.error('Error fetching prenatal period response:', err);
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch prenatal period response')
      );
    } finally {
      setLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Delete the questionnaire response
  const deleteResponse = useCallback(async () => {
    if (!questionnaireResponse?.id) {
      return;
    }

    try {
      await medplum.deleteResource('QuestionnaireResponse', questionnaireResponse.id);
      // Clear the response from state
      setQuestionnaireResponse(null);
      // Optionally refetch to ensure consistency
      await fetchData();
    } catch (err) {
      console.error('Error deleting prenatal period response:', err);
      throw err;
    }
  }, [medplum, questionnaireResponse, fetchData]);

  // Check if patient was conceived via IVF
  const isIVFConception = useMemo(() => {
    if (!questionnaireResponse?.item) {
      return false;
    }

    // Find the conception method question (q2) - directly in items, not nested in a group
    const conceptionMethodItem = questionnaireResponse.item.find(
      (item: QuestionnaireResponseItem) => item.linkId === 'q2'
    );

    const conceptionMethod = conceptionMethodItem?.answer?.[0]?.valueString;

    // If not assisted reproduction, return false
    if (conceptionMethod !== 'Υποβοηθούμενη αναπαραγωγή') {
      return false;
    }

    // Find the method question (q29) - directly in items, not nested in a group
    const methodItem = questionnaireResponse.item.find(
      (item: QuestionnaireResponseItem) => item.linkId === 'q29'
    );

    const method = methodItem?.answer?.[0]?.valueString;

    // Check if method is IVF
    return method === 'IVF';
  }, [questionnaireResponse]);

  return {
    questionnaire,
    questionnaireResponse,
    loading,
    error,
    refetch: fetchData,
    deleteResponse,
    isIVFConception,
  };
};
